import { Direction } from '../../../../windows/helpers/adjustWindow';
import { VisibleArea } from '../../../helpers/selectionHelper';
import { UseCellSelection } from '../../../hooks/useCellSelection';
import { UseSheetTheme } from '../../../hooks/useSheetTheme';
import { SelectionOperationType } from '../../../types';

interface AreaSelectionBorderProps
  extends VisibleArea,
    Pick<
      UseSheetTheme,
      | 'extendSelectionBorderColor'
      | 'extendCircleBorderColor'
      | 'selectionFill'
      | 'isRtl'
    >,
    Pick<UseCellSelection, 'handleSelectionInteraction'> {
  isActive: boolean;
  isLast: boolean;
  zIndex: number;
  selecting: boolean;
  currentOp?: SelectionOperationType;
}

type BoxDirection =
  | Direction.Top
  | Direction.Right
  | Direction.Bottom
  | Direction.Left;

const AreaSelectionBorder: React.FC<AreaSelectionBorderProps> = ({
  currentOp,
  extendCircleBorderColor,
  extendSelectionBorderColor,
  selectionFill,
  zIndex,
  isRtl,
  x,
  y,
  width,
  height,
  borderTop,
  borderRight,
  borderBottom,
  borderLeft,
  isActive,
  isLast,
  selecting,
  handleSelectionInteraction,
}) => {
  let br = borderRight;
  borderRight = isRtl ? borderLeft : borderRight;
  borderLeft = isRtl ? br : borderLeft;

  const isMoveOp =
    currentOp === SelectionOperationType.ColMove ||
    currentOp === SelectionOperationType.RowMove;

  const isCurrentOpIsExtend = currentOp === SelectionOperationType.Extend;
  const borderColor = isMoveOp
    ? selectionFill
    : isCurrentOpIsExtend
    ? extendSelectionBorderColor
    : selectionFill;
  const borderStyle = !!currentOp ? 'dashed' : 'solid'; // !!currentOp
  const pointerEvents = selecting || !isActive ? 'none' : 'all';

  const renderBorderLine = (
    x: number,
    y: number,
    sizeKey: 'width' | 'height',
    size: number,
    direction: BoxDirection
    // zoom = 1.5
  ) => {
    const extraPx = 1;
    const style: React.CSSProperties = createBorderLineStyle(
      direction !== Direction.Right ? x - extraPx : x,
      direction !== Direction.Bottom ? y - extraPx : y,
      sizeKey,
      size + extraPx,
      direction,
      borderColor,
      borderStyle,
      zIndex,
      pointerEvents,
      selecting,
      currentOp === SelectionOperationType.SelectionMove
    );

    return (
      <div
        style={style}
        onMouseDown={(e) =>
          isActive &&
          handleSelectionInteraction(e, SelectionOperationType.SelectionMove)
        }
      />
    );
  };

  const circleSize = 10;
  const extendCircle = (x: number, y: number) => (
    <div
      style={{
        height: `${circleSize}px`,
        width: `${circleSize}px`,
        position: 'absolute',
        left: `${x - circleSize / 2}px`,
        top: `${y - circleSize / 2}px`,
        borderWidth: '1px',
        borderStyle: 'solid',
        borderRadius: '50%',
        borderColor: extendCircleBorderColor,
        backgroundColor: selectionFill, //selectionFill,

        cursor: 'crosshair',
        zIndex: zIndex,
        userSelect: 'none',
        pointerEvents,
      }}
      onMouseDown={(e) =>
        handleSelectionInteraction(e, SelectionOperationType.Extend)
      }
    />
  );

  return (
    <>
      {borderTop && renderBorderLine(x, y, 'width', width, Direction.Top)}
      {borderRight &&
        renderBorderLine(x + width, y, 'height', height, Direction.Right)}
      {borderBottom &&
        renderBorderLine(x, y + height, 'width', width, Direction.Bottom)}
      {borderLeft && renderBorderLine(x, y, 'height', height, Direction.Left)}

      {isLast &&
        isActive &&
        borderBottom &&
        br &&
        extendCircle(isRtl ? x : x + width, y + height)}
    </>
  );
};

// #region helper
const createBorderLineStyle = (
  x: number,
  y: number,
  sizeKey: 'width' | 'height',
  size: number,
  direction: BoxDirection,
  borderColor: string,
  borderStyle: string,
  zIndex: number,
  pointerEvents: 'none' | 'all',
  selecting: boolean,
  moveSelection: boolean
): React.CSSProperties => {
  const style: React.CSSProperties = {
    position: 'absolute',
    borderColor: borderColor,
    lineHeight: 0,
    [sizeKey]: `${size}px`,
    left: `${x}px`,
    top: `${y}px`,
    cursor: selecting && moveSelection ? 'grabbing' : 'grab',
    borderStyle: borderStyle,
    zIndex: zIndex,
    pointerEvents,
    userSelect: 'none',
  };
  const borderWidth = moveSelection ? '2px' : '1px';
  const borderThickness = 5;
  const borderThicknessInPx = `${borderThickness}px`;
  switch (direction) {
    case Direction.Top:
      style.height = borderThicknessInPx;
      style.borderTopWidth = borderWidth;
      break;
    case Direction.Right:
      style.left = `${x - borderThickness}px`;
      style.width = borderThicknessInPx;
      style.borderRightWidth = borderWidth;
      break;
    case Direction.Bottom:
      style.height = borderThicknessInPx;

      style.top = `${y - borderThickness}px`;
      style.borderBottomWidth = borderWidth;
      break;
    case Direction.Left:
      style.width = borderThicknessInPx;
      style.borderLeftWidth = borderWidth;
  }
  return style;
};
// #endregion

export default AreaSelectionBorder;
